<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        class="popup-mailjet-settings"
        @closed="$emit('close')"
    >
        <template #header>
            <div class="dialog__header">
                <div class="dialog__header--title">
                    <SaasIcon icon="mailjet" :width="56" :height="56" />
                    <div class="mt-3"><span class="bold fs-5">Mailjet</span></div>
                </div>
                <div class="dialog__header--close">
                    <CompassIconButton icon="close" @click="$emit('close')" />
                </div>
            </div>
        </template>
        <template #default>
            <div class="dialog__default">
                <el-form ref="mailjetForm" :model="mailjetConfig" :rules="rules">
                    <span>{{ $l10n.__('Connectez votre compte MailJet sur Sellsy.') }}</span>

                    <el-divider></el-divider>

                    <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>

                    <span class="semibold mb-1 fs-4">{{ $l10n.__('Installation') }}</span>

                    <div class="mb-3">
                        <span>
                            {{
                                $l10n.__(
                                    'Connectez-vous à votre compte Mailjet pour récupérer votre clé API - Mon compte puis Clé API Principale - et entrez la ci-dessous.',
                                )
                            }}
                        </span>
                        <span>
                            {{ $l10n.__('Pour en savoir plus vous pouvez consulter cette ') }}

                            <a
                                class="semibold pointer hover-primary-100"
                                target="_blank"
                                href="https://help.sellsy.com/hc/connecter-sellsy-avec-ses-outils/mailjet"
                                >{{ $l10n.__('documentation') }}</a
                            >.
                        </span>
                    </div>

                    <el-form-item prop="apikey" :error="errors.get('apikey')">
                        <template #label
                            ><span class="semibold mb-1 gray-100">
                                {{ $l10n.__("Clé d'API") }}
                            </span></template
                        >
                        <el-input
                            v-model="mailjetConfig.apikey"
                            placeholder="Renseigner"
                        ></el-input>
                    </el-form-item>

                    <el-form-item prop="secretkey" :error="errors.get('secretkey')">
                        <template #label>
                            <span class="semibold mb-1 gray-100">
                                {{ $l10n.__('Clé secrète') }}
                            </span>
                        </template>
                        <el-input
                            v-model="mailjetConfig.secretkey"
                            placeholder="Renseigner"
                        ></el-input>
                    </el-form-item>

                    <div v-if="mode === 'settings'" class="flex-column synchro-contact">
                        <span class="semibold fs-4 mb-1">
                            {{ $l10n.__('Synchroniser les contacts') }}
                        </span>

                        <div v-if="!firstConnection && !keysChanged" class="flex-column">
                            <span class="mb-3">
                                {{
                                    $l10n.__(
                                        'Vous pouvez synchroniser vos contacts Sellsy sur votre compte Mailjet.',
                                    )
                                }}
                            </span>

                            <el-form-item prop="contacts_list_id" class="ov-hidden">
                                <template #label>
                                    <span class="semibold mb-1 gray-100">
                                        {{ $l10n.__('Liste par défaut') }}
                                    </span>
                                </template>
                                <select-input
                                    v-model="mailjetConfig.contacts_list_id"
                                    mode="edit"
                                    labelColumn="name"
                                    idColumn="id"
                                    :selectNothing="$t.__('common.labels.none')"
                                    :appendToBody="true"
                                    :options="contactsLists"
                                    :placeholder="$t.__('common.actions.select_default_list')"
                                >
                                </select-input>
                            </el-form-item>
                        </div>
                        <span class="mb-3">
                            {{
                                $l10n.__(
                                    'L’action suivante va vous permettre d’activer la synchronisation Mailjet pour les contacts de vos clients/prospects/fournisseurs en un seul clic.',
                                )
                            }}
                        </span>

                        <el-form-item prop="syncroEnabled">
                            <radio-input
                                v-model="mailjetConfig.enabled"
                                mode="edit"
                                :choices="syncChoices"
                            ></radio-input>
                        </el-form-item>

                        <el-form-item prop="mcp_syncroTypeContact" class="ov-hidden">
                            <select-input
                                v-model="mailjetConfig.filter"
                                :appendToBody="true"
                                mode="edit"
                                :options="filters"
                            />
                        </el-form-item>
                    </div>
                </el-form>
            </div>
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    type="secondary"
                    :label="$t.__('common.actions.cancel')"
                    @click="$emit('close')"
                />
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    :loading="isFormSubmitted"
                    class="ml-3"
                    @click="onSubmitForm"
                />
            </div>
        </template>
        <preloader :display="loading" />
    </CompassDialog>
</template>

<script>
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import FormMixins from '../../mixins/FormMixins';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import MailJetService from 'api/providerMarketing/MailJetService';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

export default {
    components: {
        SaasIcon,
        RadioInput,
        SelectInput,
        Preloader,
    },
    mixins: [FormMixins],
    props: {
        mode: {
            type: String,
            required: true,
        },
        callback: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            showModal: true,
            mailjetConfig: {
                apikey: '',
                secretkey: '',
                contacts_list_id: null,
                enabled: true,
                filter: 'all',
            },
            contactsLists: [],
            rules: {
                apikey: [
                    {
                        required: true,
                        message: this.$l10n.__("Merci de renseigner une clé d'API."),
                        trigger: 'blur',
                    },
                    {
                        max: 64,
                        message: this.$l10n.__("La clé d'API est trop longue"),
                        trigger: 'blur',
                    },
                ],
                secretkey: [
                    {
                        required: true,
                        message: this.$l10n.__('Merci de renseigner une clé secrète.'),
                        trigger: 'blur',
                    },
                    {
                        max: 64,
                        message: this.$l10n.__('La clé secrète est trop longue'),
                        trigger: 'blur',
                    },
                ],
            },
            filters: [
                {
                    label: this.$l10n.__('Toutes les sociétés'),
                    value: 'all',
                },
                {
                    label: this.$l10n.__('Client'),
                    value: 'clients',
                },
                {
                    label: this.$l10n.__('Prospect'),
                    value: 'prospects',
                },
                {
                    label: this.$l10n.__('Fournisseur'),
                    value: 'suppliers',
                },
            ],
            syncChoices: [
                {
                    label: this.$l10n.__('Activer la synchronisation'),
                    value: true,
                },
                {
                    label: this.$l10n.__('Désactiver la synchronisation'),
                    value: false,
                },
            ],
            loading: false,
            isFormSubmitted: false,
            firstConnection: true,
            error: null,
            originalKeys: {
                apikey: '',
                secretkey: '',
            },
        };
    },
    computed: {
        keysChanged() {
            return (
                this.mailjetConfig.apikey !== this.originalKeys.apikey ||
                this.mailjetConfig.secretkey !== this.originalKeys.secretkey
            );
        },
    },
    created() {
        this.loading = true;
        MailJetService.getConfig()
            .then((response) => {
                this.mailjetConfig.apikey = response.data.api_key;
                this.mailjetConfig.secretkey = response.data.api_secret;
                this.originalKeys.apikey = response.data.api_key;
                this.originalKeys.secretkey = response.data.api_secret;
                this.mailjetConfig.contacts_list_id =
                    response.data.contacts_synchronization.contacts_list_id === 0
                        ? null
                        : response.data.contacts_synchronization.contacts_list_id;
                this.mailjetConfig.enabled = response.data.contacts_synchronization.enabled;
                this.mailjetConfig.filter = response.data.contacts_synchronization.filter;
                this.contactsLists = response.data.contacts_lists;
                this.firstConnection = false;
            })
            .catch((error) => {
                if (error.response.data.error.code !== 404) {
                    this.error = error.response.data.error.message;
                }
            })
            .finally(() => {
                this.loading = false;
            });
    },
    methods: {
        onSubmitForm() {
            this.error = null;
            this.submitForm('mailjetForm');
        },
        // eslint-disable-next-line vue/no-unused-properties
        save() {
            this.isFormSubmitted = true;
            const settings = {
                api_key: this.mailjetConfig.apikey,
                api_secret: this.mailjetConfig.secretkey,
                contacts_synchronization: {
                    enabled: this.mailjetConfig.enabled,
                    filter: this.mailjetConfig.filter,
                },
            };
            let method = 'POST';
            if (!this.firstConnection) {
                method = 'PUT';
                settings.contacts_synchronization.contacts_list_id =
                    this.mailjetConfig.contacts_list_id;
                if (this.keysChanged) {
                    settings.contacts_synchronization.contacts_list_id = null;
                }
            }
            MailJetService.saveConfig(settings, method)
                .then(() => {
                    EventBus.emit(this.callback, 2);
                    if (this.mode === 'settings') {
                        Push.success(this.$l10n.__('Modifications bien prises en compte.'));
                    }
                    this.$emit('close');
                })
                .catch((err) => {
                    this.errors.setOneError('apikey', err.response.data.error.details.api_key);
                })
                .finally(() => {
                    this.isFormSubmitted = false;
                });
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.popup-mailjet-settings {
    .dialog {
        &__header {
            display: flex;
            justify-content: space-between;
            padding: 24px 32px;

            &--title {
                display: flex;
                flex-direction: column;
            }

            &--close {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
            }
        }

        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }

    span {
        line-height: 20px;
    }

    .el-form {
        .el-form-item {
            .el-form-item__label {
                margin-bottom: 4px;
                line-height: 20px;
            }
        }

        .el-form-item__content {
            .el-radio-group .el-radio {
                margin-right: 16px;
                color: $gray-100;

                .el-radio__input > input {
                    margin: 0;
                }

                .el-radio__label {
                    vertical-align: middle;
                }
            }

            .el-radio-group .el-radio:last-child {
                margin-right: 0;
            }
        }
    }

    .btn-update-contact {
        margin-bottom: 10px;
    }
}
</style>
